'use client';

import { c as _c } from "react/compiler-runtime";
import { Check, RotateCcw } from 'lucide-react';
import { useMemo, useState } from 'react';
import type { PrV2VersionOption } from './hooks/use-pr-v2-version-comparison';
import type { PrV2ComparisonChoice, PrV2VersionComparison } from './lib/pr-v2-comparison-choices';
import { PR_V2_TOOLBAR_TEXT_BUTTON_CLASSNAME } from '../shared/pr-v2-toolbar-styles';
import { findOptionBySha } from './hooks/use-pr-v2-version-comparison';
import { getCompactVersionLabel, getPrV2ComparisonChoices } from './lib/pr-v2-comparison-choices';
import { DropdownMenuLabel, DropdownMenuSeparator } from '@/components/ui/dropdown-menu';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Select, SelectContent, SelectItem, SelectTrigger } from '@/components/ui/select';
import { cn } from '@/lib/utils';
import { formatShortDate, getFromNow } from '@/lib/utils/date-formatters';
export type PrV2VersionComparisonPickerProps = {
  versionOptions: PrV2VersionOption[];
  selectedBaseSha: string | null;
  selectedHeadSha: string | null;
  isComparisonLoading: boolean;
  hasMeaningfulComparison: boolean;
  hasUserComparisonOverride: boolean;
  lastReviewedVersionSha: string | null;
  lastReviewedSubmittedAt: string | null;
  setComparisonAction: (comparison: PrV2VersionComparison | null) => void;
  resetComparisonAction: () => void;
};
type ComparisonShortcutProps = {
  label: string;
  sublabel: string | null;
  isActive: boolean;
  isLoading: boolean;
  onSelect: () => void;
  testId?: string;
};
const COMPARISON_SHORTCUT_TEST_IDS: Partial<Record<PrV2ComparisonChoice['id'], string>> = {
  'all-changes': 'pr-v2-all-changes-shortcut',
  'previous-push': 'pr-v2-previous-push-shortcut'
};
function findLatestHeadForBase(options: PrV2VersionOption[], baseSha: string): PrV2VersionOption | null {
  const baseOption = findOptionBySha(options, baseSha);
  if (!baseOption) {
    return null;
  }
  for (let index = options.length - 1; index >= 0; index -= 1) {
    const candidate = options[index];
    if (candidate && candidate.index > baseOption.index) {
      return candidate;
    }
  }
  return null;
}
function isHeadSameOrOlderThanBase(options: PrV2VersionOption[], baseSha: string | null, headSha: string): boolean {
  const baseOption = findOptionBySha(options, baseSha);
  const headOption = findOptionBySha(options, headSha);
  if (!baseOption || !headOption) {
    return false;
  }
  return headOption.index <= baseOption.index;
}
function getVersionOptionTag(option: PrV2VersionOption): string | null {
  if (option.isDefaultBase) {
    return 'default';
  }
  if (option.isDefaultHead) {
    return 'latest';
  }
  return null;
}
function SelectedVersionTriggerValue(t0) {
  const $ = _c(12);
  const {
    option
  } = t0;
  let t1;
  if ($[0] !== option) {
    t1 = getVersionOptionTag(option);
    $[0] = option;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const tag = t1;
  const t2 = option.isDefaultHead ? "Latest" : "Base";
  let t3;
  if ($[2] !== option.label || $[3] !== t2) {
    t3 = getCompactVersionLabel(option.label, t2);
    $[2] = option.label;
    $[3] = t2;
    $[4] = t3;
  } else {
    t3 = $[4];
  }
  const compactLabel = t3;
  let t4;
  if ($[5] !== compactLabel) {
    t4 = <span className="truncate">{compactLabel}</span>;
    $[5] = compactLabel;
    $[6] = t4;
  } else {
    t4 = $[6];
  }
  let t5;
  if ($[7] !== tag) {
    t5 = tag ? <span className="shrink-0 text-zinc-400">{`(${tag})`}</span> : null;
    $[7] = tag;
    $[8] = t5;
  } else {
    t5 = $[8];
  }
  let t6;
  if ($[9] !== t4 || $[10] !== t5) {
    t6 = <div className="flex min-w-0 items-center gap-1.5">{t4}{t5}</div>;
    $[9] = t4;
    $[10] = t5;
    $[11] = t6;
  } else {
    t6 = $[11];
  }
  return t6;
}
function VersionOptionLabel(t0) {
  const $ = _c(13);
  const {
    option,
    defaultTag,
    lastReviewedOption,
    lastReviewedDateLabel
  } = t0;
  const isLastReviewedVersion = lastReviewedOption?.id === option.id;
  let t1;
  if ($[0] !== option.label) {
    t1 = <span className="truncate">{option.label}</span>;
    $[0] = option.label;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  if ($[2] !== defaultTag) {
    t2 = defaultTag ? <span className="shrink-0 text-zinc-400">{`(${defaultTag})`}</span> : null;
    $[2] = defaultTag;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  let t3;
  if ($[4] !== t1 || $[5] !== t2) {
    t3 = <div className="flex min-w-0 items-center gap-2">{t1}{t2}</div>;
    $[4] = t1;
    $[5] = t2;
    $[6] = t3;
  } else {
    t3 = $[6];
  }
  let t4;
  if ($[7] !== isLastReviewedVersion || $[8] !== lastReviewedDateLabel) {
    t4 = isLastReviewedVersion ? <div className="text-[11px] text-zinc-400">{lastReviewedDateLabel ? `Your last review · ${lastReviewedDateLabel}` : "Your last review"}</div> : null;
    $[7] = isLastReviewedVersion;
    $[8] = lastReviewedDateLabel;
    $[9] = t4;
  } else {
    t4 = $[9];
  }
  let t5;
  if ($[10] !== t3 || $[11] !== t4) {
    t5 = <div className="min-w-0">{t3}{t4}</div>;
    $[10] = t3;
    $[11] = t4;
    $[12] = t5;
  } else {
    t5 = $[12];
  }
  return t5;
}
function ComparisonShortcut(t0) {
  const $ = _c(18);
  const {
    label,
    sublabel,
    isActive,
    isLoading,
    onSelect,
    testId
  } = t0;
  const isDisabled = isLoading || isActive;
  const t1 = isDisabled && "cursor-not-allowed opacity-50 hover:bg-transparent";
  let t2;
  if ($[0] !== t1) {
    t2 = cn("flex w-full items-start justify-between gap-3 rounded-sm px-2 py-1.5 text-left text-xs text-zinc-300 transition-colors hover:bg-zinc-900", t1);
    $[0] = t1;
    $[1] = t2;
  } else {
    t2 = $[1];
  }
  let t3;
  if ($[2] !== label) {
    t3 = <span>{label}</span>;
    $[2] = label;
    $[3] = t3;
  } else {
    t3 = $[3];
  }
  let t4;
  if ($[4] !== sublabel) {
    t4 = sublabel ? <span className="text-[11px] text-zinc-500">{sublabel}</span> : null;
    $[4] = sublabel;
    $[5] = t4;
  } else {
    t4 = $[5];
  }
  let t5;
  if ($[6] !== t3 || $[7] !== t4) {
    t5 = <span className="flex min-w-0 flex-col">{t3}{t4}</span>;
    $[6] = t3;
    $[7] = t4;
    $[8] = t5;
  } else {
    t5 = $[8];
  }
  let t6;
  if ($[9] !== isActive) {
    t6 = isActive ? <Check className="mt-0.5 size-3.5 shrink-0 text-zinc-400" /> : null;
    $[9] = isActive;
    $[10] = t6;
  } else {
    t6 = $[10];
  }
  let t7;
  if ($[11] !== isDisabled || $[12] !== onSelect || $[13] !== t2 || $[14] !== t5 || $[15] !== t6 || $[16] !== testId) {
    t7 = <button type="button" data-testid={testId} className={t2} disabled={isDisabled} onClick={onSelect}>{t5}{t6}</button>;
    $[11] = isDisabled;
    $[12] = onSelect;
    $[13] = t2;
    $[14] = t5;
    $[15] = t6;
    $[16] = testId;
    $[17] = t7;
  } else {
    t7 = $[17];
  }
  return t7;
}
export function PrV2VersionComparisonPicker(t0) {
  const $ = _c(102);
  const {
    versionOptions,
    selectedBaseSha,
    selectedHeadSha,
    isComparisonLoading,
    hasMeaningfulComparison,
    hasUserComparisonOverride,
    lastReviewedVersionSha,
    lastReviewedSubmittedAt,
    setComparisonAction,
    resetComparisonAction
  } = t0;
  let t1;
  if ($[0] !== selectedBaseSha || $[1] !== versionOptions) {
    t1 = findOptionBySha(versionOptions, selectedBaseSha) ?? versionOptions[0] ?? null;
    $[0] = selectedBaseSha;
    $[1] = versionOptions;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  const selectedBaseOption = t1;
  let t2;
  if ($[3] !== selectedHeadSha || $[4] !== versionOptions) {
    t2 = findOptionBySha(versionOptions, selectedHeadSha) ?? versionOptions[versionOptions.length - 1] ?? null;
    $[3] = selectedHeadSha;
    $[4] = versionOptions;
    $[5] = t2;
  } else {
    t2 = $[5];
  }
  const selectedHeadOption = t2;
  let t3;
  if ($[6] !== lastReviewedVersionSha || $[7] !== versionOptions) {
    t3 = findOptionBySha(versionOptions, lastReviewedVersionSha);
    $[6] = lastReviewedVersionSha;
    $[7] = versionOptions;
    $[8] = t3;
  } else {
    t3 = $[8];
  }
  const lastReviewedOption = t3;
  let t4;
  if ($[9] !== lastReviewedVersionSha || $[10] !== selectedBaseSha || $[11] !== selectedHeadSha || $[12] !== versionOptions) {
    t4 = getPrV2ComparisonChoices({
      versionOptions,
      selectedBaseSha,
      selectedHeadSha,
      lastReviewedVersionSha
    });
    $[9] = lastReviewedVersionSha;
    $[10] = selectedBaseSha;
    $[11] = selectedHeadSha;
    $[12] = versionOptions;
    $[13] = t4;
  } else {
    t4 = $[13];
  }
  const comparisonChoices = t4;
  let t5;
  bb0: {
    if (!lastReviewedSubmittedAt) {
      t5 = null;
      break bb0;
    }
    let t6;
    if ($[14] !== lastReviewedSubmittedAt) {
      t6 = formatShortDate(lastReviewedSubmittedAt);
      $[14] = lastReviewedSubmittedAt;
      $[15] = t6;
    } else {
      t6 = $[15];
    }
    const formatted = t6;
    t5 = formatted || null;
  }
  const lastReviewedDateLabel = t5;
  let t6;
  bb1: {
    const t7 = selectedBaseOption?.label;
    let t8;
    if ($[16] !== t7) {
      t8 = getCompactVersionLabel(t7, "Base");
      $[16] = t7;
      $[17] = t8;
    } else {
      t8 = $[17];
    }
    const baseLabel = t8;
    const t9 = selectedHeadOption?.label;
    let t10;
    if ($[18] !== t9) {
      t10 = getCompactVersionLabel(t9, "Latest");
      $[18] = t9;
      $[19] = t10;
    } else {
      t10 = $[19];
    }
    const headLabel = t10;
    if (selectedBaseOption?.isDefaultBase) {
      t6 = headLabel;
      break bb1;
    }
    t6 = `${baseLabel} -> ${headLabel}`;
  }
  const comparisonDisplayLabel = t6;
  const [comparisonPickerOpenedAt, setComparisonPickerOpenedAt] = useState(_temp);
  let t7;
  bb2: {
    if (!lastReviewedSubmittedAt || !lastReviewedDateLabel) {
      t7 = lastReviewedDateLabel;
      break bb2;
    }
    let t8;
    if ($[20] !== comparisonPickerOpenedAt || $[21] !== lastReviewedSubmittedAt) {
      t8 = getFromNow(lastReviewedSubmittedAt, {
        now: comparisonPickerOpenedAt
      });
      $[20] = comparisonPickerOpenedAt;
      $[21] = lastReviewedSubmittedAt;
      $[22] = t8;
    } else {
      t8 = $[22];
    }
    const lastReviewedAgo = t8;
    t7 = `${lastReviewedAgo} (${lastReviewedDateLabel})`;
  }
  const lastReviewShortcutSublabel = t7;
  if (!hasMeaningfulComparison || !selectedBaseOption || !selectedHeadOption) {
    return null;
  }
  let t8;
  if ($[23] !== isComparisonLoading || $[24] !== selectedHeadOption.id || $[25] !== setComparisonAction || $[26] !== versionOptions) {
    t8 = nextBaseSha => {
      if (isComparisonLoading) {
        return;
      }
      const currentHeadSha = selectedHeadOption.id;
      const shouldKeepHead = !isHeadSameOrOlderThanBase(versionOptions, nextBaseSha, currentHeadSha);
      const fallbackHead = findLatestHeadForBase(versionOptions, nextBaseSha);
      setComparisonAction({
        baseSha: nextBaseSha,
        headSha: shouldKeepHead ? currentHeadSha : fallbackHead?.id ?? currentHeadSha
      });
    };
    $[23] = isComparisonLoading;
    $[24] = selectedHeadOption.id;
    $[25] = setComparisonAction;
    $[26] = versionOptions;
    $[27] = t8;
  } else {
    t8 = $[27];
  }
  const onComparisonBaseChange = t8;
  let t9;
  if ($[28] !== isComparisonLoading || $[29] !== selectedBaseOption.id || $[30] !== setComparisonAction) {
    t9 = nextHeadSha => {
      if (isComparisonLoading) {
        return;
      }
      setComparisonAction({
        baseSha: selectedBaseOption.id,
        headSha: nextHeadSha
      });
    };
    $[28] = isComparisonLoading;
    $[29] = selectedBaseOption.id;
    $[30] = setComparisonAction;
    $[31] = t9;
  } else {
    t9 = $[31];
  }
  const onComparisonHeadChange = t9;
  let t10;
  if ($[32] !== isComparisonLoading || $[33] !== setComparisonAction) {
    t10 = choice => {
      if (isComparisonLoading || choice.isActive) {
        return;
      }
      setComparisonAction(choice.comparison);
    };
    $[32] = isComparisonLoading;
    $[33] = setComparisonAction;
    $[34] = t10;
  } else {
    t10 = $[34];
  }
  const onComparisonShortcutSelect = t10;
  let t11;
  if ($[35] === Symbol.for("react.memo_cache_sentinel")) {
    t11 = open => {
      if (open) {
        setComparisonPickerOpenedAt(Date.now());
      }
    };
    $[35] = t11;
  } else {
    t11 = $[35];
  }
  const onPopoverOpenChange = t11;
  const hasShortcuts = comparisonChoices.length > 0;
  const t12 = isComparisonLoading && "cursor-not-allowed opacity-80";
  let t13;
  if ($[36] !== t12) {
    t13 = cn(PR_V2_TOOLBAR_TEXT_BUTTON_CLASSNAME, "max-w-[148px] bg-transparent", t12);
    $[36] = t12;
    $[37] = t13;
  } else {
    t13 = $[37];
  }
  let t14;
  if ($[38] !== comparisonDisplayLabel) {
    t14 = <span className="truncate">{comparisonDisplayLabel}</span>;
    $[38] = comparisonDisplayLabel;
    $[39] = t14;
  } else {
    t14 = $[39];
  }
  let t15;
  if ($[40] !== isComparisonLoading || $[41] !== t13 || $[42] !== t14) {
    t15 = <PopoverTrigger asChild={true}><button type="button" aria-busy={isComparisonLoading} disabled={isComparisonLoading} className={t13} aria-label="Compare PR versions">{t14}</button></PopoverTrigger>;
    $[40] = isComparisonLoading;
    $[41] = t13;
    $[42] = t14;
    $[43] = t15;
  } else {
    t15 = $[43];
  }
  let t16;
  if ($[44] === Symbol.for("react.memo_cache_sentinel")) {
    t16 = <DropdownMenuLabel className="px-2 pb-1.5 pt-0.5 text-[10px] font-medium uppercase tracking-wide text-zinc-500">Compare versions</DropdownMenuLabel>;
    $[44] = t16;
  } else {
    t16 = $[44];
  }
  let t17;
  if ($[45] !== comparisonChoices || $[46] !== isComparisonLoading || $[47] !== lastReviewShortcutSublabel || $[48] !== onComparisonShortcutSelect) {
    let t18;
    if ($[50] !== isComparisonLoading || $[51] !== lastReviewShortcutSublabel || $[52] !== onComparisonShortcutSelect) {
      t18 = choice_0 => <ComparisonShortcut key={choice_0.id} label={choice_0.label} sublabel={choice_0.id === "last-review" ? lastReviewShortcutSublabel : choice_0.rangeLabel} isActive={choice_0.isActive} isLoading={isComparisonLoading} onSelect={() => onComparisonShortcutSelect(choice_0)} testId={COMPARISON_SHORTCUT_TEST_IDS[choice_0.id]} />;
      $[50] = isComparisonLoading;
      $[51] = lastReviewShortcutSublabel;
      $[52] = onComparisonShortcutSelect;
      $[53] = t18;
    } else {
      t18 = $[53];
    }
    t17 = comparisonChoices.map(t18);
    $[45] = comparisonChoices;
    $[46] = isComparisonLoading;
    $[47] = lastReviewShortcutSublabel;
    $[48] = onComparisonShortcutSelect;
    $[49] = t17;
  } else {
    t17 = $[49];
  }
  let t18;
  if ($[54] !== hasShortcuts) {
    t18 = hasShortcuts ? <DropdownMenuSeparator className="-mx-1 my-1 bg-zinc-800" /> : null;
    $[54] = hasShortcuts;
    $[55] = t18;
  } else {
    t18 = $[55];
  }
  let t19;
  if ($[56] === Symbol.for("react.memo_cache_sentinel")) {
    t19 = <p className="w-10 shrink-0 text-xs text-zinc-400">Base</p>;
    $[56] = t19;
  } else {
    t19 = $[56];
  }
  const t20 = selectedBaseOption.id;
  let t21;
  if ($[57] !== selectedBaseOption) {
    t21 = <SelectTrigger aria-label="Base comparison version" className="h-7 flex-1 border-zinc-700 bg-zinc-900 px-2 text-xs text-zinc-200 hover:bg-zinc-800 focus:ring-0 focus:ring-offset-0"><div className="min-w-0 pr-2"><SelectedVersionTriggerValue option={selectedBaseOption} /></div></SelectTrigger>;
    $[57] = selectedBaseOption;
    $[58] = t21;
  } else {
    t21 = $[58];
  }
  let t22;
  if ($[59] !== lastReviewedDateLabel || $[60] !== lastReviewedOption || $[61] !== versionOptions) {
    t22 = versionOptions.map(option => {
      const hasNewerHead = Boolean(findLatestHeadForBase(versionOptions, option.id));
      return <SelectItem key={`base-${option.id}`} value={option.id} textValue={option.label} disabled={!hasNewerHead} className={cn("text-xs", !hasNewerHead && "cursor-not-allowed opacity-50")}><VersionOptionLabel option={option} defaultTag={option.isDefaultBase ? "default" : null} lastReviewedOption={lastReviewedOption} lastReviewedDateLabel={lastReviewedDateLabel} /></SelectItem>;
    });
    $[59] = lastReviewedDateLabel;
    $[60] = lastReviewedOption;
    $[61] = versionOptions;
    $[62] = t22;
  } else {
    t22 = $[62];
  }
  let t23;
  if ($[63] !== t22) {
    t23 = <SelectContent className="max-w-[220px] border-zinc-700 bg-zinc-950">{t22}</SelectContent>;
    $[63] = t22;
    $[64] = t23;
  } else {
    t23 = $[64];
  }
  let t24;
  if ($[65] !== isComparisonLoading || $[66] !== onComparisonBaseChange || $[67] !== selectedBaseOption.id || $[68] !== t21 || $[69] !== t23) {
    t24 = <div className="flex items-center gap-2">{t19}<Select value={t20} onValueChange={onComparisonBaseChange} disabled={isComparisonLoading}>{t21}{t23}</Select></div>;
    $[65] = isComparisonLoading;
    $[66] = onComparisonBaseChange;
    $[67] = selectedBaseOption.id;
    $[68] = t21;
    $[69] = t23;
    $[70] = t24;
  } else {
    t24 = $[70];
  }
  let t25;
  if ($[71] === Symbol.for("react.memo_cache_sentinel")) {
    t25 = <p className="w-10 shrink-0 text-xs text-zinc-400">Head</p>;
    $[71] = t25;
  } else {
    t25 = $[71];
  }
  const t26 = selectedHeadOption.id;
  let t27;
  if ($[72] !== selectedHeadOption) {
    t27 = <SelectTrigger aria-label="Head comparison version" className="h-7 flex-1 border-zinc-700 bg-zinc-900 px-2 text-xs text-zinc-200 hover:bg-zinc-800 focus:ring-0 focus:ring-offset-0"><div className="min-w-0 pr-2"><SelectedVersionTriggerValue option={selectedHeadOption} /></div></SelectTrigger>;
    $[72] = selectedHeadOption;
    $[73] = t27;
  } else {
    t27 = $[73];
  }
  let t28;
  if ($[74] !== lastReviewedDateLabel || $[75] !== lastReviewedOption || $[76] !== selectedBaseOption.id || $[77] !== versionOptions) {
    t28 = versionOptions.map(option_0 => {
      const isDisabled = isHeadSameOrOlderThanBase(versionOptions, selectedBaseOption.id, option_0.id);
      return <SelectItem key={`head-${option_0.id}`} value={option_0.id} textValue={option_0.label} disabled={isDisabled} className={cn(isDisabled && "cursor-not-allowed opacity-50")}><VersionOptionLabel option={option_0} defaultTag={option_0.isDefaultHead ? "latest" : null} lastReviewedOption={lastReviewedOption} lastReviewedDateLabel={lastReviewedDateLabel} /></SelectItem>;
    });
    $[74] = lastReviewedDateLabel;
    $[75] = lastReviewedOption;
    $[76] = selectedBaseOption.id;
    $[77] = versionOptions;
    $[78] = t28;
  } else {
    t28 = $[78];
  }
  let t29;
  if ($[79] !== t28) {
    t29 = <SelectContent className="max-w-[220px] border-zinc-700 bg-zinc-950">{t28}</SelectContent>;
    $[79] = t28;
    $[80] = t29;
  } else {
    t29 = $[80];
  }
  let t30;
  if ($[81] !== isComparisonLoading || $[82] !== onComparisonHeadChange || $[83] !== selectedHeadOption.id || $[84] !== t27 || $[85] !== t29) {
    t30 = <div className="flex items-center gap-2">{t25}<Select value={t26} onValueChange={onComparisonHeadChange} disabled={isComparisonLoading}>{t27}{t29}</Select></div>;
    $[81] = isComparisonLoading;
    $[82] = onComparisonHeadChange;
    $[83] = selectedHeadOption.id;
    $[84] = t27;
    $[85] = t29;
    $[86] = t30;
  } else {
    t30 = $[86];
  }
  let t31;
  if ($[87] !== t24 || $[88] !== t30) {
    t31 = <div className="space-y-2 p-2">{t24}{t30}</div>;
    $[87] = t24;
    $[88] = t30;
    $[89] = t31;
  } else {
    t31 = $[89];
  }
  let t32;
  if ($[90] !== hasUserComparisonOverride || $[91] !== isComparisonLoading || $[92] !== resetComparisonAction) {
    t32 = hasUserComparisonOverride ? <><DropdownMenuSeparator className="-mx-1 my-1 bg-zinc-800" /><button type="button" className={cn("flex w-full items-center gap-2 rounded-sm px-2 py-1.5 text-left text-xs text-zinc-300 transition-colors hover:bg-zinc-900", isComparisonLoading && "cursor-not-allowed opacity-50 hover:bg-transparent")} disabled={isComparisonLoading} onClick={resetComparisonAction}><RotateCcw className="size-3.5 text-zinc-400" />Reset</button></> : null;
    $[90] = hasUserComparisonOverride;
    $[91] = isComparisonLoading;
    $[92] = resetComparisonAction;
    $[93] = t32;
  } else {
    t32 = $[93];
  }
  let t33;
  if ($[94] !== t17 || $[95] !== t18 || $[96] !== t31 || $[97] !== t32) {
    t33 = <PopoverContent align="start" className="min-w-[200px] max-w-[220px] border-zinc-800 bg-zinc-950 p-2 shadow-md" onOpenAutoFocus={_temp2}>{t16}{t17}{t18}{t31}{t32}</PopoverContent>;
    $[94] = t17;
    $[95] = t18;
    $[96] = t31;
    $[97] = t32;
    $[98] = t33;
  } else {
    t33 = $[98];
  }
  let t34;
  if ($[99] !== t15 || $[100] !== t33) {
    t34 = <div className="ml-auto min-w-0 shrink"><Popover onOpenChange={onPopoverOpenChange}>{t15}{t33}</Popover></div>;
    $[99] = t15;
    $[100] = t33;
    $[101] = t34;
  } else {
    t34 = $[101];
  }
  return t34;
}
function _temp2(event) {
  return event.preventDefault();
}
function _temp() {
  return Date.now();
}